import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

const FONT = "'JetBrains Mono', 'Fira Code', monospace";
const ACCENT = '#4f8fff';
const WHITE = '#e6edf3';
const MUTED = '#484f58';
const CHAR_DELAY = 40;
const SESSION_KEY = 'ot-boot-played';

/* ── Starfield canvas ── */
function AuthStarfield() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    interface Star {
      x: number; y: number; r: number; baseAlpha: number;
      speed: number; phase: number; twinkle: boolean;
    }

    const stars: Star[] = [];
    for (let i = 0; i < 200; i++) {
      const bright = Math.random() > 0.75;
      stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: bright ? Math.random() * 1.2 + 0.6 : Math.random() * 0.7 + 0.2,
        baseAlpha: bright ? 0.6 + Math.random() * 0.3 : 0.15 + Math.random() * 0.2,
        speed: Math.random() * 0.003 + 0.001,
        phase: Math.random() * Math.PI * 2,
        twinkle: bright,
      });
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        ctx.fillStyle = `rgba(255,255,255,${s.baseAlpha})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      return () => window.removeEventListener('resize', resize);
    }

    const animate = (t: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const s of stars) {
        let a = s.baseAlpha;
        if (s.twinkle) {
          a = s.baseAlpha * (Math.sin(t * s.speed + s.phase) * 0.4 + 0.6);
        }
        if (s.twinkle) {
          const hue = (s.phase * 30) % 60;
          ctx.fillStyle = `hsla(${210 + hue}, 60%, 85%, ${a})`;
        } else {
          ctx.fillStyle = `rgba(255,255,255,${a})`;
        }
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        if (s.twinkle && a > 0.45) {
          ctx.fillStyle = `rgba(180,200,255,${a * 0.06})`;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: [
          'radial-gradient(ellipse 50% 40% at 20% 30%, rgba(79,143,255,0.06), transparent)',
          'radial-gradient(ellipse 40% 35% at 75% 70%, rgba(167,139,250,0.04), transparent)',
          'radial-gradient(ellipse 30% 25% at 50% 50%, rgba(56,189,248,0.03), transparent)',
        ].join(', '),
      }} />
      <canvas ref={canvasRef} style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
      }} />
    </>
  );
}

/* ── Inject keyframes ── */
const STYLE_ID = 'ot-boot-keyframes';
function ensureKeyframes() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes otCursorBlink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0; }
    }
    @keyframes otGlowPulse {
      0%, 100% { text-shadow: 0 0 20px rgba(79,143,255,0.3), 0 0 40px rgba(79,143,255,0.15); }
      50% { text-shadow: 0 0 30px rgba(79,143,255,0.5), 0 0 60px rgba(79,143,255,0.25); }
    }
    @keyframes otFadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes otGridFade {
      from { opacity: 0; }
      to   { opacity: 0.03; }
    }
  `;
  document.head.appendChild(style);
}

/* ── Boot sequence lines ── */
interface BootLine {
  prefix: string;
  hasBar: boolean;
  suffix: string;
}

const BOOT_LINES: BootLine[] = [
  { prefix: '> initializing tool_os...', hasBar: false, suffix: '' },
  { prefix: '> loading runtime          ', hasBar: true, suffix: ' done' },
  { prefix: '> connecting providers     ', hasBar: true, suffix: ' done' },
  { prefix: '> mounting workspace       ', hasBar: true, suffix: ' done' },
  { prefix: '> tool_os ready', hasBar: false, suffix: '' },
];

/* ── Progress bar component ── */
function ProgressBar({ active, done }: { active: boolean; done: boolean }) {
  return (
    <span style={{ color: ACCENT }}>
      [
      <span style={{
        display: 'inline-block',
        width: '16ch',
        overflow: 'hidden',
        verticalAlign: 'bottom',
      }}>
        <span style={{
          display: 'inline-block',
          width: done ? '16ch' : active ? '16ch' : '0ch',
          transition: active && !done ? 'width 0.8s ease-in-out' : 'none',
          overflow: 'hidden',
          color: ACCENT,
        }}>
          {'████████████████'}
        </span>
      </span>
      ]
    </span>
  );
}

/* ── Boot terminal ── */
function BootTerminal({
  onComplete,
  skip,
}: {
  onComplete: () => void;
  skip: boolean;
}) {
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [barActive, setBarActive] = useState(false);
  const [barDone, setBarDone] = useState(false);
  const [suffixShown, setSuffixShown] = useState(false);
  const [completedLines, setCompletedLines] = useState<string[]>([]);
  const [opacity, setOpacity] = useState(1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (skip) {
      onComplete();
      return;
    }

    const line = BOOT_LINES[lineIndex];
    if (!line) {
      // All lines done — fade out
      setTimeout(() => {
        setOpacity(0);
        setTimeout(onComplete, 600);
      }, 400);
      return;
    }

    // Phase: typing prefix
    if (charIndex < line.prefix.length) {
      timerRef.current = setTimeout(() => {
        setCharIndex(c => c + 1);
      }, CHAR_DELAY);
      return;
    }

    // Phase: progress bar
    if (line.hasBar && !barActive) {
      setBarActive(true);
      timerRef.current = setTimeout(() => {
        setBarDone(true);
        setTimeout(() => {
          setSuffixShown(true);
          setTimeout(() => {
            // Move to next line
            setCompletedLines(prev => [...prev, renderLineText(lineIndex, true)]);
            setLineIndex(i => i + 1);
            setCharIndex(0);
            setBarActive(false);
            setBarDone(false);
            setSuffixShown(false);
          }, 200);
        }, 150);
      }, 800);
      return;
    }

    // No bar — move to next line after brief pause
    if (!line.hasBar && charIndex >= line.prefix.length) {
      timerRef.current = setTimeout(() => {
        setCompletedLines(prev => [...prev, renderLineText(lineIndex, true)]);
        setLineIndex(i => i + 1);
        setCharIndex(0);
      }, 300);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIndex, charIndex, barActive, barDone, skip]);

  function renderLineText(idx: number, full: boolean): string {
    const l = BOOT_LINES[idx];
    if (full) {
      return l.prefix + (l.hasBar ? '[████████████████]' + l.suffix : '');
    }
    return l.prefix;
  }

  if (skip) return null;

  const currentLine = BOOT_LINES[lineIndex];

  return (
    <div style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontFamily: FONT,
      fontSize: 14,
      color: '#8b949e',
      whiteSpace: 'pre',
      lineHeight: 2,
      opacity,
      transition: 'opacity 0.5s ease, transform 0.5s ease',
      ...(opacity < 1 ? { transform: 'translate(-50%, -55%)' } : {}),
    }}>
      {/* Completed lines */}
      {completedLines.map((text, i) => (
        <div key={i} style={{ color: '#484f58' }}>
          {text.startsWith('> tool_os') ? (
            <>
              <span style={{ color: ACCENT }}>{'> '}</span>
              <span style={{ color: WHITE }}>tool</span>
              <span style={{ color: ACCENT }}>_os</span>
              <span style={{ color: '#484f58' }}> ready</span>
            </>
          ) : (
            colorBootLine(text)
          )}
        </div>
      ))}

      {/* Current line being typed */}
      {currentLine && (
        <div>
          {colorBootLine(currentLine.prefix.slice(0, charIndex))}
          {charIndex >= currentLine.prefix.length && currentLine.hasBar && (
            <>
              <ProgressBar active={barActive} done={barDone} />
              {suffixShown && <span style={{ color: '#34d399' }}>{currentLine.suffix}</span>}
            </>
          )}
          {/* Blinking cursor on active line */}
          {charIndex < currentLine.prefix.length && (
            <span style={{
              animation: 'otCursorBlink 0.8s step-end infinite',
              color: ACCENT,
            }}>_</span>
          )}
        </div>
      )}
    </div>
  );
}

function colorBootLine(text: string): React.ReactNode {
  // Color the > prompt in accent, rest in muted
  if (text.startsWith('> ')) {
    const rest = text.slice(2);
    // Color tool_os specially
    if (rest.startsWith('tool_os')) {
      return (
        <>
          <span style={{ color: ACCENT }}>{'> '}</span>
          <span style={{ color: WHITE }}>tool</span>
          <span style={{ color: ACCENT }}>_os</span>
          <span style={{ color: '#484f58' }}>{rest.slice(7)}</span>
        </>
      );
    }
    return (
      <>
        <span style={{ color: ACCENT }}>{'> '}</span>
        <span style={{ color: '#8b949e' }}>{rest}</span>
      </>
    );
  }
  return <span>{text}</span>;
}

/* ── GitHub icon ── */
function GitHubIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
    </svg>
  );
}

/* ── Main LoginPage ── */
export function LoginPage() {
  const { githubOAuthConfigured } = useAuth();
  const [loading, setLoading] = useState(false);
  const [ghHovered, setGhHovered] = useState(false);
  const [email, setEmail] = useState('');
  const [github, setGithub] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    const hash = window.location.hash;
    if (hash.includes('auth_error=github_not_allowed')) {
      return 'Your GitHub account is not allowed to access this OpenTool instance.';
    }
    return null;
  });
  const [reqBtnHovered, setReqBtnHovered] = useState(false);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSending, setMagicSending] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  const alreadyPlayed = sessionStorage.getItem(SESSION_KEY) === '1';
  const [phase, setPhase] = useState<'boot' | 'hero'>(alreadyPlayed ? 'hero' : 'boot');
  const [loginVisible, setLoginVisible] = useState(alreadyPlayed);
  const [logoVisible, setLogoVisible] = useState(alreadyPlayed);

  useEffect(() => { ensureKeyframes(); }, []);

  const handleBootComplete = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, '1');
    setPhase('hero');
    setTimeout(() => setLogoVisible(true), 100);
    setTimeout(() => setLoginVisible(true), 700);
  }, []);

  const handleSkip = useCallback(() => {
    if (phase === 'boot') handleBootComplete();
  }, [phase, handleBootComplete]);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/auth/github/login');
      const data = await resp.json();
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data.error) {
        setError(data.error);
        setLoading(false);
      }
    } catch {
      setError('Failed to connect to server');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email.trim()) { setError('Email is required'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch('/api/request-access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), github: github.trim() || undefined }),
      });
      const data = await resp.json();
      if (data.ok) setSubmitted(true);
      else setError(data.error || 'Something went wrong');
    } catch {
      setError('Failed to connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '11px 14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(79,143,255,0.15)',
    borderRadius: 8,
    color: WHITE,
    fontSize: 13,
    fontFamily: FONT,
    fontWeight: 400,
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxSizing: 'border-box' as const,
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 10,
    color: MUTED,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    marginBottom: 5,
    textAlign: 'left' as const,
  };

  return (
    <div
      onClick={handleSkip}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: '#000000',
        fontFamily: FONT,
        cursor: phase === 'boot' ? 'pointer' : 'default',
        overflow: 'hidden',
      }}
    >
      <AuthStarfield />

      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage:
          `linear-gradient(rgba(79,143,255,0.03) 1px, transparent 1px),
           linear-gradient(90deg, rgba(79,143,255,0.03) 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        animation: 'otGridFade 2s ease forwards',
        opacity: 0,
      }} />

      {phase === 'boot' && (
        <BootTerminal onComplete={handleBootComplete} skip={alreadyPlayed} />
      )}

      {phase === 'hero' && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          overflowY: 'auto',
          padding: '40px 0',
        }}>
          {/* Logo */}
          <div style={{
            opacity: logoVisible ? 1 : 0,
            transform: logoVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
            textAlign: 'center',
            marginBottom: 36,
            flexShrink: 0,
          }}>
            <h1 style={{
              margin: 0, fontFamily: FONT, fontSize: 64, fontWeight: 700,
              lineHeight: 1, textShadow: '0 0 40px rgba(79,143,255,0.3)', userSelect: 'none',
            }}>
              <span style={{
                color: ACCENT, animation: 'otGlowPulse 3s ease-in-out infinite',
                display: 'inline-block',
              }}>{'>'}</span>
              {' '}
              <span style={{ color: WHITE }}>tool</span>
              <span style={{ color: ACCENT }}>_os</span>
            </h1>
            <p style={{
              color: MUTED, fontSize: 12, margin: '10px 0 0',
              letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 400,
            }}>
              by OpenTool
            </p>
          </div>

          {/* Card container */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              opacity: loginVisible ? 1 : 0,
              transform: loginVisible ? 'translateY(0)' : 'translateY(16px)',
              transition: 'opacity 0.5s ease, transform 0.5s ease',
              width: 400,
              maxWidth: '92vw',
              background: 'rgba(255,255,255,0.015)',
              border: '1px solid rgba(79,143,255,0.1)',
              borderRadius: 16,
              padding: '28px 28px 24px',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              flexShrink: 0,
            }}
          >
            {/* ── Sign in section ── */}
            {githubOAuthConfigured && (
              <>
                <p style={{
                  color: '#8b949e', fontSize: 12, margin: '0 0 14px',
                  textAlign: 'center', letterSpacing: '0.04em',
                }}>
                  Already have access?
                </p>
                <button
                  onClick={handleLogin}
                  disabled={loading}
                  onMouseEnter={() => setGhHovered(true)}
                  onMouseLeave={() => setGhHovered(false)}
                  style={{
                    width: '100%',
                    padding: '13px 24px',
                    background: ghHovered && !loading
                      ? 'rgba(79,143,255,0.08)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${ghHovered && !loading ? 'rgba(79,143,255,0.4)' : 'rgba(79,143,255,0.15)'}`,
                    borderRadius: 10,
                    color: WHITE, fontSize: 13, fontFamily: FONT, fontWeight: 500,
                    cursor: loading ? 'wait' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    transition: 'all 0.2s ease',
                    boxShadow: ghHovered && !loading
                      ? '0 0 20px rgba(79,143,255,0.15), 0 0 40px rgba(79,143,255,0.05)' : 'none',
                  }}
                >
                  <GitHubIcon />
                  {loading ? 'Redirecting...' : 'Sign in with GitHub'}
                </button>

                {/* ── OR sign in with email ── */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  margin: '18px 0',
                }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                  <span style={{ color: '#30363d', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' as const }}>
                    or sign in with email
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </div>

                {magicLinkSent ? (
                  <div style={{
                    padding: '12px 16px', background: 'rgba(52,211,153,0.04)',
                    border: '1px solid rgba(52,211,153,0.2)', borderRadius: 8,
                    textAlign: 'center',
                  }}>
                    <p style={{ color: '#34d399', fontSize: 12, margin: 0 }}>Check your email for a login link</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="email" placeholder="you@company.com"
                      value={magicEmail} onChange={(e) => setMagicEmail(e.target.value)}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.4)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.15)'; }}
                      style={{
                        ...inputStyle, flex: 1,
                      }}
                    />
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!magicEmail.trim()) return;
                        setMagicSending(true);
                        try {
                          await fetch('/api/auth/magic-link', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: magicEmail.trim() }),
                          });
                          setMagicLinkSent(true);
                        } catch { setError('Failed to send login link'); }
                        setMagicSending(false);
                      }}
                      disabled={magicSending || !magicEmail.trim()}
                      style={{
                        padding: '0 16px', background: 'rgba(79,143,255,0.06)',
                        border: '1px solid rgba(79,143,255,0.25)', borderRadius: 8,
                        color: '#4f8fff', fontSize: 12, fontFamily: FONT, fontWeight: 600,
                        cursor: magicSending ? 'wait' : 'pointer',
                        whiteSpace: 'nowrap' as const, transition: 'all 0.2s ease',
                        opacity: !magicEmail.trim() ? 0.5 : 1,
                      }}
                    >
                      {magicSending ? '...' : 'Send link'}
                    </button>
                  </div>
                )}

                {/* ── OR request access ── */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  margin: '18px 0',
                }}>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                  <span style={{ color: '#30363d', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase' as const }}>
                    or request access
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </div>
              </>
            )}

            {/* ── Request access form ── */}
            {submitted ? (
              <div style={{
                textAlign: 'center', padding: '24px 16px',
                background: 'rgba(52,211,153,0.04)',
                border: '1px solid rgba(52,211,153,0.2)',
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 22, marginBottom: 10 }}>
                  <span style={{ color: '#34d399' }}>&#10003;</span>
                </div>
                <p style={{ color: WHITE, fontSize: 13, margin: '0 0 6px', fontWeight: 500 }}>
                  Request received
                </p>
                <p style={{ color: MUTED, fontSize: 11, margin: 0, lineHeight: 1.6 }}>
                  We'll be in touch at <span style={{ color: '#38bdf8' }}>{email}</span>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {!githubOAuthConfigured && (
                  <p style={{
                    color: '#8b949e', fontSize: 12, margin: '0 0 4px',
                    textAlign: 'center', lineHeight: 1.5,
                  }}>
                    Request demo access
                  </p>
                )}

                <div>
                  <label style={labelStyle}>Email address *</label>
                  <input
                    type="email" required placeholder="you@company.com"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.4)'; e.target.style.boxShadow = '0 0 16px rgba(79,143,255,0.1)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.15)'; e.target.style.boxShadow = 'none'; }}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>GitHub handle <span style={{ opacity: 0.5 }}>(optional)</span></label>
                  <div style={{ position: 'relative' }}>
                    <span style={{
                      position: 'absolute', left: 12, top: '50%',
                      transform: 'translateY(-50%)', color: MUTED, fontSize: 13, pointerEvents: 'none',
                    }}>@</span>
                    <input
                      type="text" placeholder="username"
                      value={github} onChange={(e) => setGithub(e.target.value.replace(/^@/, ''))}
                      onFocus={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.4)'; e.target.style.boxShadow = '0 0 16px rgba(79,143,255,0.1)'; }}
                      onBlur={(e) => { e.target.style.borderColor = 'rgba(79,143,255,0.15)'; e.target.style.boxShadow = 'none'; }}
                      style={{ ...inputStyle, paddingLeft: 28 }}
                    />
                  </div>
                </div>

                <button
                  type="submit" disabled={submitting}
                  onMouseEnter={() => setReqBtnHovered(true)}
                  onMouseLeave={() => setReqBtnHovered(false)}
                  style={{
                    width: '100%', padding: '12px 24px', marginTop: 2,
                    background: reqBtnHovered && !submitting
                      ? 'rgba(52,211,153,0.1)' : 'rgba(52,211,153,0.04)',
                    border: `1px solid ${reqBtnHovered && !submitting ? 'rgba(52,211,153,0.4)' : 'rgba(52,211,153,0.2)'}`,
                    borderRadius: 10,
                    color: '#34d399', fontSize: 13, fontFamily: FONT, fontWeight: 600,
                    cursor: submitting ? 'wait' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: reqBtnHovered && !submitting
                      ? '0 0 20px rgba(52,211,153,0.15), 0 0 40px rgba(52,211,153,0.05)' : 'none',
                  }}
                >
                  {submitting ? 'Submitting...' : 'Request Access'}
                </button>
              </form>
            )}

            {/* Error */}
            {error && (
              <p style={{
                color: '#f87171', fontSize: 11, margin: '12px 0 0',
                padding: '8px 12px',
                background: 'rgba(248,113,113,0.08)',
                borderRadius: 8, border: '1px solid rgba(248,113,113,0.2)',
                lineHeight: 1.5,
              }}>
                {error}
              </p>
            )}

            {/* Alpha badge */}
            <p style={{
              color: '#21262d', fontSize: 10, margin: '14px 0 0',
              textAlign: 'center', letterSpacing: '0.06em',
            }}>
              alpha — access by request only
            </p>
          </div>

          {/* Version */}
          <p style={{
            position: 'absolute', bottom: 20,
            color: '#21262d', fontSize: 10, letterSpacing: '0.04em', margin: 0,
          }}>
            <span style={{ color: '#4f8fff', fontWeight: 700 }}>{__GIT_SHA__}</span>
          </p>
        </div>
      )}
    </div>
  );
}
